import { ApiError, unwrap, type Schemas } from '@mrigl/api-client';
import { Alert, Button, Card, CheckboxGroup, TextField } from '@mrigl/ui';
import { useCallback, useEffect, useState, type SubmitEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../api.js';
import { Loading } from '../../components.js';
import { useI18n, type I18n } from '../../i18n.js';
import { display, useStore, type StoreDetails } from '../catalog/shared.js';

type State = Schemas['CodSettingsState'];
type Blocked = Schemas['BlockedPhoneList']['items'][number];

function codError(t: I18n['t'], failure: unknown): string {
  if (failure instanceof ApiError && failure.code === 'validation-failed') {
    return t('admin.cod.error.phone');
  }
  return errorMessage(t, failure);
}

/** "" → null, otherwise a whole number (the input enforces the bounds). */
const optionalNumber = (value: string) => (value.trim() === '' ? null : Number(value));

/** Cash-on-delivery settings and the numbers refused cash on delivery. */
export function CodSettingsPage() {
  const { storeId = '' } = useParams();
  const { t, locale } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const [state, setState] = useState<State | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    void unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/cod-settings', { params: { path: { storeId } } }),
    )
      .then(setState)
      .catch((failure: unknown) => {
        setLoadError(errorMessage(t, failure));
      });
  }, [storeId, t]);

  const error = storeError ?? loadError;
  if (error !== null) return <Alert tone="error">{error}</Alert>;
  if (store === null || state === null) return <Loading />;
  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}`}>{display(store.name, locale, store, store.slug)}</Link>
      </p>
      <h1 className="mrigl-heading">{t('admin.cod.title')}</h1>
      <p>{t('admin.cod.intro')}</p>
      <SettingsForm store={store} initial={state} />
      <BlockedNumbers store={store} />
    </div>
  );
}

function SettingsForm({
  store,
  initial,
}: {
  readonly store: StoreDetails;
  readonly initial: State;
}) {
  const { t } = useI18n();
  const canManage = store.permissions.includes('checkout.manage');
  const [version, setVersion] = useState(initial.version);
  const [customer, setCustomer] = useState(initial.settings.customerConfirmation);
  const [hours, setHours] = useState(String(initial.settings.autoCancelHours ?? ''));
  const [threshold, setThreshold] = useState(String(initial.settings.refusalThreshold ?? ''));
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      const saved = await unwrap(
        api.PUT('/admin-api/v1/stores/{storeId}/cod-settings', {
          params: { path: { storeId: store.storeId } },
          body: {
            version,
            settings: {
              customerConfirmation: customer,
              autoCancelHours: optionalNumber(hours),
              refusalThreshold: optionalNumber(threshold),
            },
          },
        }),
      );
      setVersion(saved.version);
      setNotice(t('admin.cod.saved'));
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="mrigl-card mrigl-stack"
      aria-label={t('admin.cod.title')}
      onSubmit={(event) => void save(event)}
    >
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <CheckboxGroup
        legend={t('admin.cod.title')}
        options={[{ value: 'customer', label: t('admin.cod.customerConfirmation') }]}
        values={customer ? ['customer'] : []}
        onChange={(values) => {
          setCustomer(values.includes('customer'));
        }}
      />
      <TextField
        type="number"
        min={1}
        max={720}
        step={1}
        label={t('admin.cod.autoCancelHours')}
        hint={t('admin.cod.autoCancelHint')}
        value={hours}
        onChange={(event) => {
          setHours(event.target.value);
        }}
      />
      <TextField
        type="number"
        min={1}
        max={50}
        step={1}
        label={t('admin.cod.refusalThreshold')}
        hint={t('admin.cod.refusalHint')}
        value={threshold}
        onChange={(event) => {
          setThreshold(event.target.value);
        }}
      />
      {canManage ? (
        <Button type="submit" busy={busy}>
          {t('admin.save')}
        </Button>
      ) : null}
    </form>
  );
}

function BlockedNumbers({ store }: { readonly store: StoreDetails }) {
  const { t, formatDateTime } = useI18n();
  const storeId = store.storeId;
  const canManage = store.permissions.includes('orders.manage');
  const [items, setItems] = useState<Blocked[]>([]);
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const result = await unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/cod-blocked-phones', {
        params: { path: { storeId } },
      }),
    );
    setItems(result.items);
  }, [storeId]);

  useEffect(() => {
    load().catch((failure: unknown) => {
      setError(errorMessage(t, failure));
    });
  }, [load, t]);

  async function block(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/cod-blocked-phones', {
          params: { path: { storeId } },
          body: { phone: phone.trim(), reason: reason.trim() === '' ? null : reason.trim() },
        }),
      );
      setPhone('');
      setReason('');
      setNotice(t('admin.cod.blockedDone'));
      await load();
    } catch (failure) {
      setError(codError(t, failure));
    } finally {
      setBusy(false);
    }
  }

  async function unblock(blockId: string) {
    setNotice(null);
    setError(null);
    try {
      await unwrap(
        api.DELETE('/admin-api/v1/stores/{storeId}/cod-blocked-phones/{blockId}', {
          params: { path: { storeId, blockId } },
        }),
      );
      setNotice(t('admin.cod.unblockedDone'));
      await load();
    } catch (failure) {
      setError(codError(t, failure));
    }
  }

  return (
    <Card labelledBy="cod-blocked">
      <h2 id="cod-blocked" className="mrigl-subheading">
        {t('admin.cod.blocked.title')}
      </h2>
      <p className="mrigl-muted">{t('admin.cod.blocked.hint')}</p>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {items.length === 0 ? (
        <p className="mrigl-muted">{t('admin.cod.blocked.none')}</p>
      ) : (
        <ul className="mrigl-list">
          {items.map((item) => (
            <li key={item.blockId}>
              {t('admin.cod.blocked.entry', { hint: item.hint })} — {formatDateTime(item.createdAt)}
              {item.reason === null ? null : (
                <>
                  {' — '}
                  <span dir="auto">{item.reason}</span>
                </>
              )}{' '}
              {canManage ? (
                <Button variant="secondary" onClick={() => void unblock(item.blockId)}>
                  {t('admin.cod.blocked.remove', { hint: item.hint })}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {canManage ? (
        <form
          className="mrigl-stack"
          aria-label={t('admin.cod.blocked.add')}
          onSubmit={(event) => void block(event)}
        >
          <TextField
            type="tel"
            dir="ltr"
            required
            label={t('admin.cod.blocked.phone')}
            value={phone}
            onChange={(event) => {
              setPhone(event.target.value);
            }}
          />
          <TextField
            label={t('admin.cod.blocked.reason')}
            maxLength={300}
            dir="auto"
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
          <Button type="submit" busy={busy}>
            {t('admin.cod.blocked.add')}
          </Button>
        </form>
      ) : null}
    </Card>
  );
}
